/**
 * Geometry and persistence rules for the Biggles window.
 *
 * Pure so the clamping and the parse-what-we-stored rules can be pinned
 * without a DOM. The hook (`use-biggles-window.ts`) owns the event wiring.
 */

import type { CSSProperties } from 'react'

export type WindowMode ='floating' | 'docked-left' | 'docked-right'

export interface FloatingRect {
  x: number
  y: number
  width: number
  height: number
}

export interface WindowLayout {
  mode: WindowMode
  /** Last floating geometry — kept while docked so undocking restores it. */
  floating: FloatingRect
}

export interface Viewport {
  width: number
  height: number
}

export const WINDOW_MODES: readonly WindowMode[] = ['floating', 'docked-left', 'docked-right']

/** Default floating size — wide enough for tool-result cards, short of a sidebar. */
export const DEFAULT_FLOATING_SIZE = { width: 420, height: 640 } as const
export const MIN_FLOATING_SIZE = { width: 320, height: 360 } as const
/** Docked width; the page content is not reflowed, the window overlays the edge. */
export const DOCK_WIDTH = 440
/** Margin kept between a floating window and the viewport edge. */
export const EDGE_MARGIN = 16
/**
 * How much of the window must stay on screen. A window dragged entirely off
 * screen cannot be dragged back, so the header strip always remains reachable.
 */
export const MIN_VISIBLE = 120

export const STORAGE_KEY = 'biggles-window'

/** Bottom-right resting position, matching the launcher button's corner. */
export function defaultFloatingRect(viewport: Viewport): FloatingRect {
  const width = Math.min(DEFAULT_FLOATING_SIZE.width, Math.max(MIN_FLOATING_SIZE.width, viewport.width - EDGE_MARGIN * 2))
  const height = Math.min(DEFAULT_FLOATING_SIZE.height, Math.max(MIN_FLOATING_SIZE.height, viewport.height - EDGE_MARGIN * 2))
  return {
    x: Math.max(EDGE_MARGIN, viewport.width - width - EDGE_MARGIN),
    y: Math.max(EDGE_MARGIN, viewport.height - height - EDGE_MARGIN),
    width,
    height,
  }
}

export function defaultLayout(viewport: Viewport): WindowLayout {
  return { mode: 'floating', floating: defaultFloatingRect(viewport) }
}

/**
 * Keep a floating rect usable inside the viewport: never larger than the
 * viewport, and never pushed so far out that the header cannot be grabbed.
 */
export function clampFloatingRect(rect: FloatingRect, viewport: Viewport): FloatingRect {
  const width = Math.max(MIN_FLOATING_SIZE.width, Math.min(rect.width, viewport.width - EDGE_MARGIN * 2))
  const height = Math.max(MIN_FLOATING_SIZE.height, Math.min(rect.height, viewport.height - EDGE_MARGIN * 2))

  const minX = MIN_VISIBLE - width
  const maxX = viewport.width - MIN_VISIBLE
  const minY = 0 // the header is the top edge; it must never leave the screen
  const maxY = viewport.height - MIN_VISIBLE

  return {
    x: Math.round(Math.min(Math.max(rect.x, minX), Math.max(minX, maxX))),
    y: Math.round(Math.min(Math.max(rect.y, minY), Math.max(minY, maxY))),
    width: Math.round(width),
    height: Math.round(height),
  }
}

export function clampLayout(layout: WindowLayout, viewport: Viewport): WindowLayout {
  return { mode: layout.mode, floating: clampFloatingRect(layout.floating, viewport) }
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v)
}

/**
 * Parse a persisted layout. Anything malformed — an older shape, a hand-edited
 * value, a different app's key — yields null so the caller falls back to the
 * default rather than rendering a window at NaN,NaN.
 */
export function parseStoredLayout(raw: string | null | undefined, viewport: Viewport): WindowLayout | null {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null
  const obj = parsed as Record<string, unknown>
  if (!WINDOW_MODES.includes(obj.mode as WindowMode)) return null
  const f = obj.floating
  if (!f || typeof f !== 'object') return null
  const r = f as Record<string, unknown>
  if (!isFiniteNumber(r.x) || !isFiniteNumber(r.y) || !isFiniteNumber(r.width) || !isFiniteNumber(r.height)) {
    return null
  }
  return clampLayout(
    { mode: obj.mode as WindowMode, floating: { x: r.x, y: r.y, width: r.width, height: r.height } },
    viewport
  )
}

export function serializeLayout(layout: WindowLayout): string {
  return JSON.stringify(layout)
}

/** Resolved CSS for the window's outer element, per mode. */
export function windowStyle(layout: WindowLayout, viewport: Viewport): CSSProperties {
  if (layout.mode === 'floating') {
    const r = clampFloatingRect(layout.floating, viewport)
    return { position: 'fixed', left: r.x, top: r.y, width: r.width, height: r.height }
  }
  const width = Math.min(DOCK_WIDTH, viewport.width)
  return {
    position: 'fixed',
    top: 0,
    bottom: 0,
    width,
    ...(layout.mode === 'docked-left' ? { left: 0 } : { right: 0 }),
  }
}

/** Move the floating rect by a drag delta and keep it on screen. */
export function moveFloating(layout: WindowLayout, dx: number, dy: number, viewport: Viewport): WindowLayout {
  return clampLayout(
    { ...layout, floating: { ...layout.floating, x: layout.floating.x + dx, y: layout.floating.y + dy } },
    viewport
  )
}

/** Resize from the bottom-right corner by a drag delta. */
export function resizeFloating(layout: WindowLayout, dw: number, dh: number, viewport: Viewport): WindowLayout {
  return clampLayout(
    { ...layout, floating: { ...layout.floating, width: layout.floating.width + dw, height: layout.floating.height + dh } },
    viewport
  )
}

/**
 * Dragging a floating window past the viewport edge reads as "put it there"
 * rather than "hide it", so the pointer crossing an edge docks the window.
 */
export function dockTargetForPointer(pointerX: number, viewport: Viewport): WindowMode | null {
  if (pointerX <= 0) return 'docked-left'
  if (pointerX >= viewport.width - 1) return 'docked-right'
  return null
}
